import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { monitoringAPI, employeeAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  MapPin, 
  RefreshCw, 
  Globe,
  Wifi,
  WifiOff,
  Users,
  AlertTriangle,
  Flag,
  ZoomIn,
  ZoomOut
} from 'lucide-react';
import { toast } from 'sonner';

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createIcon = (color) => new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="
    width: 24px;
    height: 24px;
    background-color: ${color};
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
  ">
    <div style="
      width: 8px;
      height: 8px;
      background-color: white;
      border-radius: 50%;
    "></div>
  </div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

const normalIcon = createIcon('#4F46E5');
const vpnIcon = createIcon('#EF4444');

// Map controls component
const MapControls = ({ onZoomToSA }) => {
  const map = useMap();
  
  return (
    <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
      <Button 
        size="sm" 
        variant="secondary" 
        className="bg-white shadow-lg"
        onClick={() => map.zoomIn()}
      >
        <ZoomIn className="w-4 h-4" />
      </Button>
      <Button 
        size="sm" 
        variant="secondary" 
        className="bg-white shadow-lg"
        onClick={() => map.zoomOut()}
      >
        <ZoomOut className="w-4 h-4" />
      </Button>
      <Button 
        size="sm" 
        variant="secondary" 
        className="bg-white shadow-lg"
        onClick={onZoomToSA}
      >
        <Flag className="w-4 h-4" />
      </Button>
    </div>
  );
};

const WorldMap = () => {
  const { user } = useAuth();
  const [mapData, setMapData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [mapRef, setMapRef] = useState(null);

  // South Africa center coordinates
  const SA_CENTER = [-28.4793, 24.6727];
  const SA_ZOOM = 5;
  const WORLD_CENTER = [0, 20];
  const WORLD_ZOOM = 2;

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchMapData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [mapRes, empRes] = await Promise.all([
        monitoringAPI.getMapData(),
        employeeAPI.getAll()
      ]);
      setMapData(mapRes.data);
      setEmployees(empRes.data);
    } catch (error) {
      console.error('Map data fetch error:', error);
      toast.error('Failed to load map data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMapData = async () => {
    try {
      setRefreshing(true);
      const response = await monitoringAPI.getMapData();
      setMapData(response.data);
    } catch (error) {
      console.error('Map refresh error:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const zoomToSouthAfrica = () => {
    if (mapRef) {
      mapRef.flyTo(SA_CENTER, SA_ZOOM, { duration: 1.5 });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading world map...</p>
        </div>
      </div>
    );
  }

  const summary = mapData?.summary || {};
  const locations = mapData?.locations || [];
  const saEmployees = locations.filter(l => l.country === 'South Africa');
  const vpnEmployees = locations.filter(l => l.vpn_detected);

  return (
    <div className="space-y-6" data-testid="world-map-page">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">South Africa</p>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {summary.south_africa || 0}
                </p>
                <p className="text-emerald-200 text-xs mt-1">employees</p>
              </div>
              <Flag className="w-10 h-10 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-600 text-sm">VPN Detected</p>
                <p className="text-3xl font-bold text-red-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {summary.vpn_detected || 0}
                </p>
                <p className="text-red-500 text-xs mt-1">employees abroad</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Total Online</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {summary.total || 0}
                </p>
              </div>
              <Users className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Countries</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {summary.countries?.length || 1}
                </p>
              </div>
              <Globe className="w-10 h-10 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* World Map */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Global Employee Locations</CardTitle>
                <CardDescription>Real-time tracking with South Africa focus</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={zoomToSouthAfrica}
                >
                  <Flag className="w-4 h-4 mr-2" />
                  Focus SA
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={fetchMapData}
                  disabled={refreshing}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="relative rounded-xl overflow-hidden" style={{ height: '500px' }}>
              <MapContainer 
                center={SA_CENTER}
                zoom={SA_ZOOM}
                style={{ height: '100%', width: '100%' }}
                ref={setMapRef}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {/* Employee Markers */}
                {locations.map((loc, index) => (
                  loc.latitude && loc.longitude ? (
                    <Marker 
                      key={index}
                      position={[loc.latitude, loc.longitude]}
                      icon={loc.vpn_detected ? vpnIcon : normalIcon}
                    >
                      <Popup>
                        <div className="min-w-[200px]">
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-3 h-3 rounded-full ${loc.vpn_detected ? 'bg-red-500' : 'bg-emerald-500'}`} />
                            <span className="font-semibold">{loc.name}</span>
                          </div>
                          <p className="text-sm text-slate-600">{loc.position}</p>
                          <p className="text-sm text-slate-500">{loc.department}</p>
                          <div className="mt-2 pt-2 border-t border-slate-100">
                            <p className="text-xs text-slate-500">
                              <MapPin className="w-3 h-3 inline mr-1" />
                              {loc.city}, {loc.country}
                            </p>
                            <p className="text-xs font-mono text-slate-400 mt-1">
                              IP: {loc.ip_address}
                            </p>
                            {loc.vpn_detected && (
                              <Badge className="mt-2 bg-red-100 text-red-700">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                VPN Detected
                              </Badge>
                            )}
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  ) : null
                ))}

                <MapControls onZoomToSA={zoomToSouthAfrica} />
              </MapContainer>

              {/* Legend */}
              <div className="absolute bottom-4 left-4 z-[1000] bg-white p-3 rounded-lg shadow-lg">
                <p className="text-xs font-semibold text-slate-700 mb-2">Legend</p>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-4 h-4 rounded-full bg-indigo-600 border-2 border-white shadow-sm" />
                  <span className="text-xs text-slate-600">South Africa ({saEmployees.length})</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-white shadow-sm" />
                  <span className="text-xs text-slate-600">VPN Detected ({vpnEmployees.length})</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Employee List */}
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Employee Locations</CardTitle>
            <CardDescription>Live status by country</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[450px] overflow-y-auto">
              {/* South Africa Section */}
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2 p-2 bg-emerald-50 rounded-lg">
                  <Flag className="w-4 h-4 text-emerald-600" />
                  <span className="font-semibold text-emerald-700">South Africa</span>
                  <Badge className="ml-auto bg-emerald-100 text-emerald-700">{saEmployees.length}</Badge>
                </div>
                {saEmployees.slice(0, 5).map((emp, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded-lg">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-emerald-100 text-emerald-600 text-xs">
                        {emp.name?.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{emp.name}</p>
                      <p className="text-xs text-slate-500">{emp.city}</p>
                    </div>
                    <Wifi className="w-4 h-4 text-emerald-500" />
                  </div>
                ))}
                {saEmployees.length > 5 && (
                  <p className="text-xs text-slate-400 text-center py-2">+{saEmployees.length - 5} more in SA</p>
                )}
              </div>

              {/* VPN Section */}
              {vpnEmployees.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2 p-2 bg-red-50 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    <span className="font-semibold text-red-700">VPN Detected</span>
                    <Badge className="ml-auto bg-red-100 text-red-700">{vpnEmployees.length}</Badge>
                  </div>
                  {vpnEmployees.map((emp, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 hover:bg-red-50 rounded-lg border border-red-100">
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="bg-red-100 text-red-600 text-xs">
                          {emp.name?.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{emp.name}</p>
                        <p className="text-xs text-red-600">{emp.city}, {emp.country}</p>
                      </div>
                      <WifiOff className="w-4 h-4 text-red-500" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WorldMap;
